import { useEffect, useState } from 'react';
import { DownloadSimple, Plus, Tag, Trash } from 'shared/icons';
import api from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { MONO_CLASS, Mono, Sku } from '../ui/Mono';
import Select from '../ui/Select';
import { useToast } from '../ui/Toast';
import { ParseDecimalInput } from './locationFields';
import { ProductPicker } from './lpnPickers';
import { ExtractListRows } from './payload';
import { useWarehouseLabels } from './warehouseLabels';
import { DownloadZplFile, SendZplToPrinter, useZplPrinters, ZplPrinterSelect } from './zplPrinting';

/**
 * IMPRIMIR LPNs (M10 r2 — fusão imprimir/receber).
 *
 * A porta ÚNICA do rolo de LPNs. O caso comum continua sendo o de 14/08 do
 * dono: *"imprimir um lote de N LPNs sem item; o operador pega o rolo e no
 * coletor bipa LPN, EAN e quantidade"*. Mas agora, OPCIONALMENTE, dá para já
 * declarar aqui:
 *   - a ORIGEM do lote (ordem de compra, devolução, produção…), e
 *   - os ITENS que cada LPN vai conter (o MESMO conteúdo nos N — decisão do
 *     dono).
 *
 * ⚠️ Declarar itens NÃO cria estoque. Os LPNs nascem em branco numa localização
 * VIRTUAL; o conteúdo declarado fica no LOTE e só vira saldo (lpn_items +
 * movimento de entrada) quando o COLETOR coloca cada LPN numa posição real. Por
 * isso "Receber LPN" deixou de ser um botão separado: receber é imprimir com
 * itens e depois colocar no lugar.
 *
 * A impressão reusa o caminho de sempre (rede via backend, USB via QZ Tray): um
 * lote é um fluxo ZPL com N formatos concatenados.
 */
const ORIGIN_TYPES = ['PURCHASE_ORDER', 'PRODUCTION_ORDER', 'RETURN', 'LEGACY', 'REPACK'];
const PURCHASE_ORDERS_LIMIT = 200;
const EMPTY_ITEM_DRAFT = { product: null, qty: '', supplierLot: '', expiryDate: '' };

let itemKeySeed = 0;

const LpnBatchModal = ({ isOpen, onClose, onCreated }) => {
    const { label } = useWarehouseLabels();
    const toast = useToast();
    const [prefixes, setPrefixes] = useState([]);
    const [deposits, setDeposits] = useState([]);
    const [prefixCode, setPrefixCode] = useState('');
    const [depositId, setDepositId] = useState('');
    const [qty, setQty] = useState('100');
    const [media, setMedia] = useState('40x60');
    const [notes, setNotes] = useState('');

    // Origem declarada do lote (opcional — colapsada por padrão = rolo em branco).
    const [withOrigin, setWithOrigin] = useState(false);
    const [originType, setOriginType] = useState('PURCHASE_ORDER');
    const [originId, setOriginId] = useState('');
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [purchaseOrdersError, setPurchaseOrdersError] = useState(null);
    const [purchaseOrdersLoading, setPurchaseOrdersLoading] = useState(false);

    // Itens declarados do lote (opcional). null no bulky = default dos produtos.
    const [withItems, setWithItems] = useState(false);
    const [items, setItems] = useState([]);
    const [itemDraft, setItemDraft] = useState(EMPTY_ITEM_DRAFT);
    const [bulkyOverride, setBulkyOverride] = useState(null);

    const [result, setResult] = useState(null);
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const { printers, localPrinters, selectedPrinter, setSelectedPrinter } = useZplPrinters(isOpen);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        setResult(null);
        setFormError(null);
        setQty('100');
        setNotes('');
        setWithOrigin(false);
        setOriginType('PURCHASE_ORDER');
        setOriginId('');
        setWithItems(false);
        setItems([]);
        setItemDraft(EMPTY_ITEM_DRAFT);
        setBulkyOverride(null);
        let cancelled = false;
        (async () => {
            try {
                const [prefixPage, depositPage] = await Promise.all([
                    api.getWarehouseLpnPrefixes({ active: 'true', limit: 100 }),
                    api.getWarehouseDeposits({ active: 'true', limit: 100 }),
                ]);
                if (cancelled) {
                    return;
                }
                const prefixRows = ExtractListRows(prefixPage);
                const depositRows = ExtractListRows(depositPage);
                setPrefixes(prefixRows);
                setDeposits(depositRows);
                setPrefixCode((current) => current || prefixRows[0]?.code || '');
                setDepositId((current) => current || depositRows[0]?.id || '');
            } catch (error) {
                if (!cancelled) {
                    setFormError({ message: error?.message || 'Erro ao carregar cadastros' });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    // Ordens de compra só quando a origem é PURCHASE_ORDER e a seção está aberta.
    useEffect(() => {
        if (!isOpen || !withOrigin || originType !== 'PURCHASE_ORDER') {
            return undefined;
        }
        let alive = true;
        setPurchaseOrdersLoading(true);
        setPurchaseOrdersError(null);
        api.getPurchaseOrders({ limit: PURCHASE_ORDERS_LIMIT, offset: 0 })
            .then((data) => {
                if (alive) {
                    setPurchaseOrders(ExtractListRows(data, ['results', 'orders']));
                }
            })
            .catch((error) => {
                if (alive) {
                    setPurchaseOrdersError(error?.message || label('wms.common.load_error'));
                    setPurchaseOrders([]);
                }
            })
            .finally(() => {
                if (alive) {
                    setPurchaseOrdersLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [isOpen, withOrigin, originType, label]);

    const busy = saving || sending;
    const closeModal = () => {
        if (!busy) {
            onClose();
        }
    };

    const derivedBulky = items.some((item) => Boolean(item.product?.is_bulky));
    const isBulky = bulkyOverride === null ? derivedBulky : bulkyOverride;

    const addItem = () => {
        if (!itemDraft.product) {
            setFormError({ field: 'item_product', message: label('wms.lpn_create.pick_product') });
            return;
        }
        const parsed = ParseDecimalInput(itemDraft.qty);
        if (parsed === null || Number.isNaN(parsed) || parsed <= 0) {
            setFormError({ field: 'item_qty', message: label('wms.lpn_create.qty_invalid') });
            return;
        }
        itemKeySeed += 1;
        setItems((previous) => [
            ...previous,
            {
                key: `item-${itemKeySeed}`,
                product: itemDraft.product,
                qty: parsed,
                supplierLot: itemDraft.supplierLot.trim(),
                expiryDate: itemDraft.expiryDate,
            },
        ]);
        setItemDraft(EMPTY_ITEM_DRAFT);
        setFormError(null);
    };

    const removeItem = (key) => {
        setItems((previous) => previous.filter((item) => item.key !== key));
    };

    // Enter nos campos do item ADICIONA o item — nunca submete o rolo.
    const handleItemFieldKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            addItem();
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFormError(null);
        const parsedQty = Number(qty);
        if (!Number.isInteger(parsedQty) || parsedQty < 1 || parsedQty > 300) {
            setFormError({ field: 'qty', message: label('wms.lpn_batch.qty_help') });
            return;
        }
        if (!prefixCode || !depositId) {
            setFormError({ message: label('wms.common.required') });
            return;
        }

        const payload = {
            prefix_code: prefixCode,
            qty: parsedQty,
            deposit_id: depositId,
            notes: notes.trim() || null,
            media,
        };
        if (withOrigin && originType) {
            payload.origin_type = originType;
            const trimmedOriginId = String(originId).trim();
            if (trimmedOriginId) {
                payload.origin_id = trimmedOriginId;
            }
        }
        if (withItems && items.length > 0) {
            payload.items = items.map((item) => ({
                product_id: item.product.product_id,
                qty: item.qty,
                ...(item.supplierLot ? { supplier_lot: item.supplierLot } : {}),
                ...(item.expiryDate ? { expiry_date: item.expiryDate } : {}),
            }));
            payload.is_bulky = isBulky;
        }

        setSaving(true);
        try {
            const created = await api.createWarehouseLpnBatch(payload);
            setResult(created);
            onCreated?.();
        } catch (error) {
            setFormError({
                message:
                    error?.code === 'NETWORK_ERROR'
                        ? label('wms.collector.error_network')
                        : error?.message || 'Erro ao gerar o rolo',
            });
        } finally {
            setSaving(false);
        }
    };

    // N formatos ZPL concatenados = o rolo inteiro numa tacada.
    const zplToPrint = result ? result.labels.map((item) => item.zpl).join('\n') : null;

    const handleSend = async () => {
        if (!zplToPrint || !selectedPrinter) {
            return;
        }
        setSending(true);
        try {
            const printerName = await SendZplToPrinter({ target: selectedPrinter, printers, zpl: zplToPrint, label });
            toast.success(`${label('wms.labels.sent')}${printerName ? ` — ${printerName}` : ''}`);
        } catch (error) {
            toast.error(error?.code === 'NETWORK_ERROR' ? label('wms.collector.error_network') : error);
        } finally {
            setSending(false);
        }
    };

    const originIsPurchaseOrder = originType === 'PURCHASE_ORDER';
    const originFreeText = originType && (!originIsPurchaseOrder || Boolean(purchaseOrdersError));

    return (
        <Modal isOpen={isOpen} onClose={closeModal} title={label('wms.lpn_batch.title')} size="lg">
            <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.lpn_batch.description')}</p>

                {!result ? (
                    <>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <Select
                                label={label('wms.lpn_create.prefix')}
                                value={prefixCode}
                                onChange={(event) => setPrefixCode(event.target.value)}
                                disabled={busy}
                            >
                                {prefixes.map((prefix) => (
                                    <option key={prefix.id} value={prefix.code}>
                                        {`${prefix.code} — ${prefix.label}`}
                                    </option>
                                ))}
                            </Select>
                            <Input
                                label={label('wms.lpn_batch.qty')}
                                type="number"
                                min={1}
                                max={300}
                                value={qty}
                                onChange={(event) => setQty(event.target.value)}
                                errorMessage={formError?.field === 'qty' ? formError.message : undefined}
                                helperText={label('wms.lpn_batch.qty_help')}
                                disabled={busy}
                                className={MONO_CLASS}
                            />
                        </div>

                        <Select
                            label={label('wms.lpn.field.deposit')}
                            value={depositId}
                            onChange={(event) => setDepositId(event.target.value)}
                            disabled={busy}
                        >
                            {deposits.map((deposit) => (
                                <option key={deposit.id} value={deposit.id}>
                                    {`${deposit.code} — ${deposit.label}`}
                                </option>
                            ))}
                        </Select>

                        <Select
                            label={label('wms.labels.media')}
                            value={media}
                            onChange={(event) => setMedia(event.target.value)}
                            disabled={busy}
                        >
                            <option value="40x60">{label('wms.labels.media_40x60')}</option>
                            <option value="40x40">{label('wms.labels.media_40x40')}</option>
                            <option value="100x150">{label('wms.labels.media_100x150')}</option>
                        </Select>

                        <Input
                            label={label('wms.lpn_batch.notes')}
                            value={notes}
                            onChange={(event) => setNotes(event.target.value)}
                            disabled={busy}
                            autoComplete="off"
                        />

                        {/* ── Origem declarada (opcional) ──────────────────────── */}
                        <section className="rounded-xl border border-mono-200 p-3 dark:border-mono-800">
                            <label className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    checked={withOrigin}
                                    onChange={(event) => setWithOrigin(event.target.checked)}
                                    disabled={busy}
                                    className="mt-1 h-4 w-4"
                                />
                                <span>
                                    <span className="block text-sm font-medium text-black dark:text-white">
                                        {label('wms.lpn_batch.with_origin')}
                                    </span>
                                    <span className="block text-xs text-mono-500 dark:text-mono-400">
                                        {label('wms.lpn_batch.origin_help')}
                                    </span>
                                </span>
                            </label>

                            {withOrigin ? (
                                <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Select
                                        label={label('wms.lpn.field.origin')}
                                        value={originType}
                                        onChange={(event) => {
                                            setOriginType(event.target.value);
                                            setOriginId('');
                                        }}
                                        disabled={busy}
                                    >
                                        {ORIGIN_TYPES.map((type) => (
                                            <option key={type} value={type}>
                                                {label(`wms.lpn_origin.${type}`)}
                                            </option>
                                        ))}
                                    </Select>
                                    {originIsPurchaseOrder && !purchaseOrdersError ? (
                                        <Select
                                            label={label('wms.lpn_create.purchase_order')}
                                            value={originId}
                                            onChange={(event) => setOriginId(event.target.value)}
                                            disabled={busy || purchaseOrdersLoading}
                                        >
                                            <option value="">{label('wms.lpn_create.select_purchase_order')}</option>
                                            {purchaseOrders.map((row) => (
                                                <option key={row.id} value={String(row.id)}>
                                                    {`${row.code}${row.supplier_name ? ` — ${row.supplier_name}` : ''}`}
                                                </option>
                                            ))}
                                        </Select>
                                    ) : originFreeText ? (
                                        <Input
                                            label={label('wms.lpn_create.origin_id')}
                                            value={originId}
                                            onChange={(event) => setOriginId(event.target.value)}
                                            disabled={busy}
                                            autoComplete="off"
                                            className={MONO_CLASS}
                                        />
                                    ) : null}
                                    {originIsPurchaseOrder && purchaseOrdersError ? (
                                        <p className="text-xs text-warning-text dark:text-warning-300 sm:col-span-2">
                                            {label('wms.lpn_create.purchase_orders_unavailable')}
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}
                        </section>

                        {/* ── Itens declarados (opcional) ──────────────────────── */}
                        <section className="rounded-xl border border-mono-200 p-3 dark:border-mono-800">
                            <label className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    checked={withItems}
                                    onChange={(event) => setWithItems(event.target.checked)}
                                    disabled={busy}
                                    className="mt-1 h-4 w-4"
                                />
                                <span>
                                    <span className="block text-sm font-medium text-black dark:text-white">
                                        {label('wms.lpn_batch.with_items')}
                                    </span>
                                    <span className="block text-xs text-mono-500 dark:text-mono-400">
                                        {label('wms.lpn_batch.items_help')}
                                    </span>
                                </span>
                            </label>

                            {withItems ? (
                                <div className="mt-3 space-y-3">
                                    {items.length > 0 ? (
                                        <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                            {items.map((item) => (
                                                <li key={item.key} className="flex items-center gap-2 py-2">
                                                    <span className="min-w-0 flex-1">
                                                        <Sku value={item.product.sku} className="text-xs font-medium" />
                                                        <span className="block truncate text-xs text-mono-500 dark:text-mono-400">
                                                            {item.product.name}
                                                        </span>
                                                        {item.supplierLot || item.expiryDate ? (
                                                            <span className="block text-xs text-mono-500 dark:text-mono-400">
                                                                {[
                                                                    item.supplierLot
                                                                        ? `${label('wms.lpn.field.supplier_lot')}: ${item.supplierLot}`
                                                                        : null,
                                                                    item.expiryDate
                                                                        ? `${label('wms.lpn.field.expiry_date')}: ${item.expiryDate}`
                                                                        : null,
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(' · ')}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                    <Mono className="shrink-0 text-sm text-black dark:text-white">
                                                        {item.qty}
                                                    </Mono>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeItem(item.key)}
                                                        disabled={busy}
                                                        aria-label={label('wms.lpn_create.remove_item')}
                                                        title={label('wms.lpn_create.remove_item')}
                                                        className="shrink-0 rounded p-1.5 transition-colors hover:bg-mono-100 disabled:opacity-50 dark:hover:bg-mono-900"
                                                    >
                                                        <Trash size={14} className="text-danger-600 dark:text-danger-400" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="rounded-lg border border-dashed border-mono-300 px-3 py-3 text-center text-xs text-mono-500 dark:border-mono-700 dark:text-mono-400">
                                            {label('wms.lpn_create.items_empty')}
                                        </p>
                                    )}

                                    <ProductPicker
                                        label={label}
                                        fieldLabel={label('wms.lpn_create.add_product')}
                                        value={itemDraft.product}
                                        onChange={(product) => setItemDraft((previous) => ({ ...previous, product }))}
                                        disabled={busy}
                                    />
                                    {formError?.field === 'item_product' ? (
                                        <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                                            {formError.message}
                                        </p>
                                    ) : null}

                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                        <Input
                                            label={label('wms.lpn.field.qty')}
                                            inputMode="decimal"
                                            value={itemDraft.qty}
                                            onChange={(event) =>
                                                setItemDraft((previous) => ({ ...previous, qty: event.target.value }))
                                            }
                                            onKeyDown={handleItemFieldKeyDown}
                                            errorMessage={formError?.field === 'item_qty' ? formError.message : undefined}
                                            disabled={busy}
                                            autoComplete="off"
                                            className={MONO_CLASS}
                                        />
                                        <Input
                                            label={label('wms.lpn.field.supplier_lot')}
                                            value={itemDraft.supplierLot}
                                            onChange={(event) =>
                                                setItemDraft((previous) => ({
                                                    ...previous,
                                                    supplierLot: event.target.value,
                                                }))
                                            }
                                            onKeyDown={handleItemFieldKeyDown}
                                            disabled={busy}
                                            autoComplete="off"
                                            className={MONO_CLASS}
                                        />
                                        <Input
                                            label={label('wms.lpn.field.expiry_date')}
                                            type="date"
                                            value={itemDraft.expiryDate}
                                            onChange={(event) =>
                                                setItemDraft((previous) => ({
                                                    ...previous,
                                                    expiryDate: event.target.value,
                                                }))
                                            }
                                            disabled={busy}
                                        />
                                    </div>
                                    <Button type="button" variant="secondary" size="sm" onClick={addItem} disabled={busy}>
                                        <Plus size={14} className="mr-1.5" />
                                        {label('wms.lpn_create.add_item')}
                                    </Button>

                                    <label className="flex items-start gap-3 pt-1">
                                        <input
                                            type="checkbox"
                                            checked={isBulky}
                                            onChange={(event) => setBulkyOverride(event.target.checked)}
                                            disabled={busy}
                                            className="mt-1 h-4 w-4"
                                        />
                                        <span>
                                            <span className="block text-sm font-medium text-black dark:text-white">
                                                {label('wms.lpn.field.is_bulky')}
                                            </span>
                                            <span className="block text-xs text-mono-500 dark:text-mono-400">
                                                {label('wms.lpn_create.is_bulky_help')}
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            ) : null}
                        </section>
                    </>
                ) : null}

                {formError && !formError.field ? (
                    <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                        {formError.message}
                    </p>
                ) : null}

                {result ? (
                    <div className="space-y-3 rounded-lg border border-mono-200 bg-mono-50 p-4 dark:border-mono-800 dark:bg-mono-950">
                        <p className="text-2xl font-semibold text-black dark:text-white">
                            {label('wms.lpn_batch.generated', { count: result.labels.length })}
                        </p>
                        <p className="text-sm text-mono-600 dark:text-mono-400">
                            <Mono>{result.labels[0]?.code}</Mono>
                            {result.labels.length > 1 ? (
                                <>
                                    {' … '}
                                    <Mono>{result.labels.at(-1)?.code}</Mono>
                                </>
                            ) : null}
                        </p>
                    </div>
                ) : null}

                {result ? (
                    <div className="space-y-3">
                        <ZplPrinterSelect
                            label={label}
                            printers={printers}
                            localPrinters={localPrinters}
                            value={selectedPrinter}
                            onChange={setSelectedPrinter}
                            disabled={busy}
                        />
                        <div className="flex flex-col gap-3 sm:flex-row">
                            <Button
                                type="button"
                                onClick={handleSend}
                                loading={sending}
                                disabled={!selectedPrinter}
                                className="flex-1"
                            >
                                <Tag size={18} className="mr-2" />
                                {label('wms.labels.send')}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                disabled={sending}
                                onClick={() => DownloadZplFile(zplToPrint, `rolo-${result.labels.length}.zpl`)}
                            >
                                <DownloadSimple size={18} className="mr-2" />
                                {label('wms.labels.download')}
                            </Button>
                        </div>
                    </div>
                ) : null}

                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                    {!result ? (
                        <Button type="submit" loading={saving} className="flex-1">
                            {label('wms.lpn_batch.generate')}
                        </Button>
                    ) : null}
                    <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto">
                        {label('wms.common.close')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default LpnBatchModal;
